import { Component, ojs, app, state, v, payload } from "modular-openscriptjs";
import { dom, tool } from "../../fotastart";
import { eParse, cleanDispose } from "../../helpers";

const h = app("h");
const broker = app("broker");
const $e = app("$e");

// How long a star click waits, "loading down" to zero, before it's
// actually saved - clicking a different star during this window just
// restarts the countdown (that's the "debounce"). A poor rating (below
// 3 stars) gets a longer window since it also has to give the customer
// time to pick a reason (or type one) before it auto-saves.
const BASE_COUNTDOWN_SECONDS = 5;
const LOW_RATING_COUNTDOWN_SECONDS = 10;
const RING_RADIUS = 15;
const RING_CIRCUMFERENCE = 2 * Math.PI * RING_RADIUS;

// Pre-filled reasons offered for a poor (below 3 star) item rating.
// Kept short since several items can be on screen at once - these are
// laid out as small inline chips, not a full list.
const ITEM_REVIEW_REASONS = [
    "Not as described",
    "Poor quality",
    "Wrong item",
    "Damaged/defective",
    "Missing parts",
];

export default class BusinessRatingModal extends Component {
    modal = null;
    items = state([]);

    // order_item_id -> setTimeout id, for items currently mid-countdown.
    timers = {};
    // order_item_id -> deadline (Date.now() timestamp) the running timer
    // above is set to fire at - lets us work out how much time was left
    // when a countdown gets paused.
    deadlines = {};
    // order_item_id -> milliseconds left, for an item whose countdown is
    // currently paused (its review offcanvas is open).
    remaining = {};
    // order_item_id -> { item, rating }, the last rating chosen for an
    // item that's mid-countdown (or was just submitted).
    pending = {};
    // order_item_id -> Set of selected pre-filled reasons.
    selectedReasons = {};
    // order_item_id -> the free-text note typed in the "Other" offcanvas.
    reviewTexts = {};

    // The single item-review offcanvas, and which item it's currently
    // open for (only one can be open at a time - the backdrop blocks
    // interaction with the rest of the list while it's up).
    reviewCanvas = null;
    reviewCanvasItemId = null;

    render(...args) {
        return h.div({ class: "d-none" }, ...args);
    }

    $$businessRating = {
        needs: {
            prompt: (ed, event) => this.showForm(ed, event),
        },
        itemRated: (ed, event) => this.onItemRated(ed, event),
        itemRatingFailed: (ed, event) => this.onItemRatingFailed(ed, event),
    };

    showForm(ed, event) {
        const { message } = eParse(ed);
        const items = message.get("items", []);

        if (!Array.isArray(items) || items.length === 0) return;

        this.cancelAllTimers();
        this.pending = {};
        this.selectedReasons = {};
        this.reviewTexts = {};
        this.items.value = items;

        if (this.modal) {
            cleanDispose(this.modal);
        }

        const content = h.div(
            {
                class: "modal-content border-0 rounded-4 shadow-lg overflow-hidden",
            },
            this.styles(),
            h.div(
                { class: "modal-body p-4" },
                this.renderHeader(),
                this.renderList(),
            ),
        );

        this.modal = tool.plainModal(content, {});

        this.modal._element?.addEventListener("hidden.bs.modal", () => {
            // Don't drop a rating the customer already tapped just because
            // they closed the sheet before the countdown finished.
            this.flushPendingTimers();
            broker.send($e.businessRating.dismissed);
        });

        this.modal.show();
        broker.send($e.businessRating.shown);
    }

    renderHeader() {
        return h.div(
            { class: "d-flex justify-content-between align-items-start mb-3" },
            h.div(
                h.h5({ class: "fw-bold mb-1" }, "Rate your order"),
                h.p(
                    { class: "text-muted small mb-0" },
                    "Tap a star for each item below - we'll save it automatically.",
                ),
            ),
            h.button({
                class: "btn-close bg-light rounded-circle p-2 flex-shrink-0",
                "data-bs-dismiss": "modal",
                aria_label: "Close",
            }),
        );
    }

    renderList() {
        return v(this.items, (itemsState) => {
            const items = itemsState.value || [];

            if (items.length === 0) {
                return h.div(
                    { class: "text-center py-4" },
                    h.i({
                        class: "fas fa-circle-check text-success mb-2",
                        style: "font-size: 2rem;",
                    }),
                    h.p(
                        { class: "mb-0 fw-semibold text-dark" },
                        "All done, thank you!",
                    ),
                );
            }

            return h.div(
                { class: "d-flex flex-column gap-2" },
                items.map((item) => this.renderItemRow(item)),
            );
        });
    }

    renderItemRow(item) {
        const id = item.order_item_id;
        const logo = item.company?.logo;
        const initialRating = 0;

        return h.div(
            {
                id: `br-row-${id}`,
                class: "br-row d-flex align-items-center gap-3 p-3 rounded-4 bg-light",
            },
            logo
                ? h.img({
                      src: logo,
                      class: "rounded-circle flex-shrink-0 bg-white",
                      style: "width: 44px; height: 44px; object-fit: cover;",
                      alt: item.company?.name || "",
                  })
                : h.div(
                      {
                          class: "rounded-circle bg-secondary-subtle d-flex align-items-center justify-content-center flex-shrink-0",
                          style: "width: 44px; height: 44px;",
                      },
                      h.i({ class: "fas fa-store text-secondary" }),
                  ),
            h.div(
                { class: "flex-grow-1 min-w-0" },
                h.div(
                    { class: "small text-muted" },
                    item.company?.name || "",
                ),
                h.div(
                    { class: "fw-semibold text-dark" },
                    `How was ${item.product_name || "this product"}?`,
                ),
                h.div(
                    { id: `br-stars-${id}`, class: "d-flex gap-1 mt-1" },
                    [1, 2, 3, 4, 5].map((r) =>
                        h.span(
                            {
                                class: "br-star",
                                data_star: r,
                                onclick: this.method("rate", [item, r]),
                            },
                            h.i({
                                class:
                                    r <= initialRating
                                        ? "fas fa-star text-warning"
                                        : "far fa-star text-secondary-subtle",
                            }),
                        ),
                    ),
                ),
                this.renderReasonsRow(item),
                h.div({
                    id: `br-error-${id}`,
                    class: "small text-danger mt-1 d-none",
                }),
            ),
            h.div(
                { id: `br-ring-${id}`, class: "br-ring flex-shrink-0 d-none" },
                h.$svg(
                    { viewBox: "0 0 36 36", width: "26", height: "26" },
                    h.$circle({
                        cx: "18",
                        cy: "18",
                        r: `${RING_RADIUS}`,
                        fill: "none",
                        stroke: "#dee2e6",
                        stroke_width: "3",
                    }),
                    h.$circle({
                        class: "br-ring-progress",
                        cx: "18",
                        cy: "18",
                        r: `${RING_RADIUS}`,
                        fill: "none",
                        // Brand purple - var(--bs-primary) isn't reliably
                        // honoured on a plain SVG presentation attribute,
                        // so this is hardcoded to match $ca-color-primary.
                        stroke: "#973e87",
                        stroke_width: "3",
                        stroke_linecap: "round",
                        stroke_dasharray: `${RING_CIRCUMFERENCE}`,
                        stroke_dashoffset: "0",
                        transform: "rotate(-90 18 18)",
                    }),
                ),
            ),
        );
    }

    // Small inline "circle" chips - not the CheckYesno toggle used
    // elsewhere - so several fit on one line without overwhelming the
    // list of items. Hidden by default; only shown for a poor rating.
    renderReasonsRow(item) {
        const id = item.order_item_id;

        return h.div(
            { id: `br-reasons-${id}`, class: "br-reasons d-flex flex-wrap gap-2 mt-2 d-none" },
            ITEM_REVIEW_REASONS.map((reason) => this.reasonChip(item, reason)),
            h.span(
                {
                    id: `br-other-chip-${id}`,
                    class: "br-reason-chip",
                    onclick: this.method("openReviewCanvas", item),
                },
                "Other",
            ),
        );
    }

    reasonChip(item, label) {
        return h.span(
            {
                class: "br-reason-chip",
                data_reason: label,
                onclick: this.method("toggleReason", [item, label]),
            },
            label,
        );
    }

    styles() {
        return h.style(`
            .br-star {
                cursor: pointer;
                font-size: 1.15rem;
                line-height: 1;
            }
            .br-row {
                transition: opacity .25s ease, max-height .25s ease,
                    margin .25s ease, padding .25s ease;
                overflow: hidden;
                max-height: 220px;
            }
            .br-row.br-row-removing {
                opacity: 0;
                max-height: 0;
                padding-top: 0 !important;
                padding-bottom: 0 !important;
                margin: 0 !important;
                border-width: 0 !important;
            }
            .br-reason-chip {
                display: inline-flex;
                align-items: center;
                padding: .25rem .65rem;
                border-radius: 999px;
                border: 1px solid #dee2e6;
                font-size: .72rem;
                line-height: 1.3;
                color: #6c757d;
                background: #fff;
                cursor: pointer;
                user-select: none;
                white-space: nowrap;
                transition: background-color .15s ease, color .15s ease,
                    border-color .15s ease;
            }
            .br-reason-chip.is-selected {
                background: var(--bs-primary);
                border-color: var(--bs-primary);
                color: #fff;
            }
        `);
    }

    // --- Rating + countdown -------------------------------------------

    countdownSeconds(rating) {
        return rating < 3 ? LOW_RATING_COUNTDOWN_SECONDS : BASE_COUNTDOWN_SECONDS;
    }

    rate(item, rating) {
        const id = item.order_item_id;

        this.setStarsDisplay(id, rating);
        this.hideError(id);

        this.pending[id] = { item, rating };

        // A fresh star tap always gets a full, fresh window - even if a
        // countdown (or a paused one) was already in progress.
        delete this.remaining[id];
        this.resetRing(id);
        this.startCountdown(id, this.countdownSeconds(rating));

        this.toggleReasonsRow(item, rating);
    }

    setStarsDisplay(id, rating) {
        const container = dom.id(`br-stars-${id}`);
        if (!container) return;

        container.querySelectorAll(".br-star").forEach((star) => {
            const r = Number(star.getAttribute("data-star"));
            const icon = star.querySelector("i");
            if (!icon) return;

            if (r <= rating) {
                icon.className = "fas fa-star text-warning";
            } else {
                icon.className = "far fa-star text-secondary-subtle";
            }
        });
    }

    toggleReasonsRow(item, rating) {
        const id = item.order_item_id;
        const row = dom.id(`br-reasons-${id}`);
        if (!row) return;

        if (rating > 0 && rating < 3) {
            row.classList.remove("d-none");
            return;
        }

        row.classList.add("d-none");

        // A rating of 3 and above doesn't need a reason - drop whatever
        // had been picked so it doesn't linger into a later low rating.
        delete this.selectedReasons[id];
        delete this.reviewTexts[id];
        row.querySelectorAll(".br-reason-chip").forEach((chip) =>
            chip.classList.remove("is-selected"),
        );
    }

    toggleReason(item, label) {
        const id = item.order_item_id;

        if (!this.selectedReasons[id]) this.selectedReasons[id] = new Set();
        const set = this.selectedReasons[id];

        if (set.has(label)) {
            set.delete(label);
        } else {
            set.add(label);
        }

        const row = dom.id(`br-reasons-${id}`);
        const chip = row
            ? Array.from(row.querySelectorAll(".br-reason-chip")).find(
                  (el) => el.getAttribute("data-reason") === label,
              )
            : null;
        if (chip) chip.classList.toggle("is-selected", set.has(label));

        // Still actively deciding - give them the full window again
        // rather than letting it run out while they're mid-selection.
        const rating = this.pending[id]?.rating;
        if (rating != null) {
            this.resetRing(id);
            this.startCountdown(id, this.countdownSeconds(rating));
        }
    }

    startCountdown(id, seconds) {
        this.clearTimer(id);
        delete this.remaining[id];

        this.deadlines[id] = Date.now() + seconds * 1000;
        this.timers[id] = setTimeout(() => {
            delete this.timers[id];
            this.commitRating(id);
        }, seconds * 1000);

        this.animateRing(id, RING_CIRCUMFERENCE, seconds);
    }

    // Freezes the countdown exactly where it is (used while the item's
    // review offcanvas is open) and remembers how long was left.
    pauseCountdown(id) {
        if (!this.timers[id]) return;

        this.clearTimer(id);
        this.remaining[id] = Math.max(0, (this.deadlines[id] || 0) - Date.now());
        this.freezeRing(id);
    }

    // Picks the countdown back up from exactly where it was paused,
    // rather than granting a fresh full window.
    resumeCountdown(id) {
        const remainingMs = this.remaining[id];
        delete this.remaining[id];

        if (remainingMs == null) return;

        if (remainingMs <= 0) {
            this.commitRating(id);
            return;
        }

        this.deadlines[id] = Date.now() + remainingMs;
        this.timers[id] = setTimeout(() => {
            delete this.timers[id];
            this.commitRating(id);
        }, remainingMs);

        this.animateRing(id, RING_CIRCUMFERENCE, remainingMs / 1000);
    }

    clearTimer(id) {
        if (this.timers[id]) {
            clearTimeout(this.timers[id]);
            delete this.timers[id];
        }
    }

    // --- Ring animation -------------------------------------------------

    resetRing(id) {
        const wrapper = dom.id(`br-ring-${id}`);
        if (!wrapper) return;
        wrapper.classList.remove("d-none");

        const progress = wrapper.querySelector(".br-ring-progress");
        if (!progress) return;

        // Snap back to "full" with no transition, force a reflow, so the
        // next animateRing() call visibly restarts from scratch.
        progress.style.transition = "none";
        progress.style.strokeDashoffset = "0";
        // eslint-disable-next-line no-unused-expressions
        progress.getBoundingClientRect();
    }

    animateRing(id, targetOffset, seconds) {
        const wrapper = dom.id(`br-ring-${id}`);
        if (!wrapper) return;
        wrapper.classList.remove("d-none");

        const progress = wrapper.querySelector(".br-ring-progress");
        if (!progress) return;

        progress.style.transition = `stroke-dashoffset ${seconds}s linear`;
        progress.style.strokeDashoffset = `${targetOffset}`;
    }

    // Cancels the transition exactly where it currently is, instead of
    // resetting or jumping it, so resuming later continues smoothly.
    freezeRing(id) {
        const wrapper = dom.id(`br-ring-${id}`);
        if (!wrapper) return;

        const progress = wrapper.querySelector(".br-ring-progress");
        if (!progress) return;

        const currentOffset =
            parseFloat(getComputedStyle(progress).strokeDashoffset) || 0;

        progress.style.transition = "none";
        progress.style.strokeDashoffset = `${currentOffset}`;
        // eslint-disable-next-line no-unused-expressions
        progress.getBoundingClientRect();
    }

    hideRing(id) {
        const wrapper = dom.id(`br-ring-${id}`);
        if (wrapper) wrapper.classList.add("d-none");
    }

    // --- "Other" review offcanvas --------------------------------------

    openReviewCanvas(item) {
        // Shouldn't normally happen - the offcanvas backdrop blocks
        // interaction with the rest of the list while one is open.
        if (this.reviewCanvas) return;

        const id = item.order_item_id;

        this.pauseCountdown(id);
        this.reviewCanvasItemId = id;

        const existingText = this.reviewTexts[id] || "";

        this.reviewCanvas = tool.offcanvas(
            h.div(
                { class: "offcanvas-body p-4" },
                h.div(
                    {
                        class: "d-flex justify-content-between align-items-start mb-3",
                    },
                    h.h5(
                        { class: "fw-bold mb-0" },
                        `Review ${item.product_name || "this item"}`,
                    ),
                    h.button({
                        class: "btn-close bg-light rounded-circle p-2",
                        "data-bs-dismiss": "offcanvas",
                        aria_label: "Close",
                    }),
                ),
                h.textarea(
                    {
                        id: "br-other-input",
                        class: "form-control bg-light border-0 p-3 rounded-3",
                        rows: "4",
                        placeholder: "Tell us what happened...",
                    },
                    existingText,
                ),
                h.button(
                    {
                        class: "btn btn-primary rounded-pill w-100 mt-3 fw-bold py-3",
                        onclick: this.method("confirmReviewCanvas"),
                    },
                    "OK",
                ),
            ),
        );

        // The offcanvas sits above an already-open modal here, which
        // Bootstrap doesn't natively stack correctly (offcanvas z-index
        // is lower than a modal's by default) - bump both it and its
        // backdrop above the rating modal so it's actually reachable.
        if (this.reviewCanvas._element) {
            this.reviewCanvas._element.style.zIndex = 1070;
        }

        this.reviewCanvas._element?.addEventListener(
            "hidden.bs.offcanvas",
            () => this.resolveReviewCanvas(),
            { once: true },
        );

        this.reviewCanvas.show();

        // The backdrop is created by Bootstrap as part of show(), so it
        // only exists a tick later.
        requestAnimationFrame(() => {
            const backdrop = document.querySelector(
                ".offcanvas-backdrop:last-of-type",
            );
            if (backdrop) backdrop.style.zIndex = 1065;
        });
    }

    confirmReviewCanvas() {
        this.reviewCanvas?.hide();
    }

    // Fires whether the customer hit "OK" or just closed/dismissed the
    // sheet - either way we save whatever they typed and let the item's
    // countdown carry on from where it was paused.
    resolveReviewCanvas() {
        if (!this.reviewCanvas) return;

        const id = this.reviewCanvasItemId;
        const textarea = dom.id("br-other-input");
        const text = (textarea?.value || "").trim();

        if (text) {
            this.reviewTexts[id] = text;
        } else {
            delete this.reviewTexts[id];
        }
        this.updateOtherChipDisplay(id, !!text);

        const canvas = this.reviewCanvas;
        this.reviewCanvas = null;
        this.reviewCanvasItemId = null;
        cleanDispose(canvas);

        this.resumeCountdown(id);
    }

    updateOtherChipDisplay(id, hasText) {
        const chip = dom.id(`br-other-chip-${id}`);
        if (chip) chip.classList.toggle("is-selected", hasText);
    }

    // --- Saving -----------------------------------------------------------

    showError(id, message) {
        const el = dom.id(`br-error-${id}`);
        if (!el) return;
        el.textContent = message;
        el.classList.remove("d-none");
    }

    hideError(id) {
        const el = dom.id(`br-error-${id}`);
        if (!el) return;
        el.classList.add("d-none");
        el.textContent = "";
    }

    commitRating(id) {
        const entry = this.pending[id];
        if (!entry) return;

        const { item, rating } = entry;

        const parts = Array.from(this.selectedReasons[id] || []);
        const customText = (this.reviewTexts[id] || "").trim();
        if (customText) parts.push(customText);

        broker.send(
            $e.businessRating.needs.rateItem,
            payload({
                orderItemId: id,
                companyId: item.company?.id,
                rating,
                review: parts.join(". "),
            }),
        );
    }

    onItemRated(ed, event) {
        const { message } = eParse(ed);
        const id = message.get("orderItemId");

        delete this.pending[id];
        delete this.selectedReasons[id];
        delete this.reviewTexts[id];
        delete this.remaining[id];
        delete this.deadlines[id];
        this.hideRing(id);
        this.removeRow(id);
    }

    onItemRatingFailed(ed, event) {
        const { message } = eParse(ed);
        const id = message.get("orderItemId");

        this.hideRing(id);
        this.showError(id, "Couldn't save that rating - tap a star to try again.");
    }

    removeRow(id) {
        const row = dom.id(`br-row-${id}`);
        if (row) {
            row.classList.add("br-row-removing");
        }

        setTimeout(() => {
            this.items.value = this.items.value.filter(
                (i) => i.order_item_id !== id,
            );

            if (this.items.value.length === 0) {
                setTimeout(() => {
                    if (this.modal) this.modal.hide();
                }, 1200);
            }
        }, 260);
    }

    cancelAllTimers() {
        for (let id in this.timers) {
            clearTimeout(this.timers[id]);
        }
        this.timers = {};
        this.remaining = {};
        this.deadlines = {};
    }

    // Instead of discarding an in-flight (mid-countdown, or paused-for-
    // review) rating when the modal closes, save it right away - the
    // customer already made their choice, closing the sheet shouldn't
    // lose it.
    flushPendingTimers() {
        // The modal is going away regardless, so just tear the offcanvas
        // down directly rather than round-tripping through its own close
        // handling.
        if (this.reviewCanvas) {
            cleanDispose(this.reviewCanvas);
            this.reviewCanvas = null;
            this.reviewCanvasItemId = null;
        }

        for (let id in this.timers) {
            clearTimeout(this.timers[id]);
            delete this.timers[id];
            this.commitRating(id);
        }

        for (let id in this.remaining) {
            delete this.remaining[id];
            this.commitRating(id);
        }
    }

    cleanUp() {
        super.cleanUp();
        this.cancelAllTimers();
        if (this.reviewCanvas) cleanDispose(this.reviewCanvas);
        if (this.modal) cleanDispose(this.modal);
    }
}

ojs(BusinessRatingModal);
